import { Field as FieldPrimitive } from "@base-ui/react/field";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { useMemo } from "react";

import { Label } from "@beweegbaas/ui/components/ui/label";
import { Separator } from "@beweegbaas/ui/components/ui/separator";
import { FieldRootContext } from "@beweegbaas/ui/lib/field-context";
import { cn, cnState } from "@beweegbaas/ui/lib/utils";

function FieldSet({ className, ...props }: React.ComponentProps<"fieldset">) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn(
        `flex flex-col gap-6 has-[>[data-slot=checkbox-group]]:gap-3
        has-[>[data-slot=radio-group]]:gap-3`,
        className,
      )}
      {...props}
    />
  );
}

function FieldLegend({
  className,
  variant = "legend",
  ...props
}: React.ComponentProps<"legend"> & { variant?: "legend" | "label" }) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn(
        `mb-3 font-medium data-[variant=label]:text-sm
        data-[variant=legend]:text-base`,
        className,
      )}
      {...props}
    />
  );
}

function FieldGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-group"
      className={cn(
        `group/field-group @container/field-group flex w-full flex-col gap-7
        data-[slot=checkbox-group]:gap-3 [&>[data-slot=field-group]]:gap-4`,
        className,
      )}
      {...props}
    />
  );
}

const fieldVariants = cva("data-invalid:text-destructive gap-2 group/field flex w-full", {
  variants: {
    orientation: {
      vertical: "flex-col [&>*]:w-full [&>.sr-only]:w-auto",
      horizontal: [
        // Layout
        "flex-row items-center",
        // Label width
        "[&>[data-slot=field-label]]:flex-auto",
        // Content alignment
        "has-[>[data-slot=field-content]]:items-start",
        // Checkbox/radio alignment
        "has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
      ],
      responsive: [
        // Base mobile styles
        "flex-col [&>*]:w-full [&>*]:grow [&>.sr-only]:w-auto",
        // Responsive desktop layout
        "@md/field-group:flex-row @md/field-group:items-center",
        // Responsive desktop width adjustments
        "@md/field-group:[&>*]:w-auto @md/field-group:[&>[data-slot=field-label]]:flex-auto",
        // Responsive desktop content alignment
        "@md/field-group:has-[>[data-slot=field-content]]:items-start",
        // Responsive desktop checkbox/radio alignment
        "@md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
      ],
      "responsive-fixed": [
        "flex-col [&>*]:w-full [&>*]:grow [&>.sr-only]:w-auto",
        // Form field width first child var --form-field-width
        "@md/field-group:[&>*:first-child]:min-w-[var(--form-field-width,180px)]",
        // Responsive desktop layout
        "@md/field-group:flex-row @md/field-group:items-center",
        // Responsive desktop width adjustments
        "@md/field-group:[&>*]:w-full @md/field-group:[&>[data-slot=field-label]]:flex-auto",
        // Max width styles
        "@md/field-group:[&>*:nth-child(2)]:max-w-[var(--form-field-content-width,700px)]",
        // Responsive desktop content alignment
        "@md/field-group:has-[>[data-slot=field-content]]:items-start",
        // Responsive desktop checkbox/radio alignment
        "@md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
      ],
    },
  },
  defaultVariants: {
    orientation: "vertical",
  },
});

function Field({
  className,
  orientation = "vertical",
  invalid,
  "data-invalid": dataInvalid,
  ...props
}: FieldPrimitive.Root.Props &
  VariantProps<typeof fieldVariants> & {
    /** Legacy styling hook — mapped to Base UI's `invalid` prop. */
    "data-invalid"?: boolean;
  }) {
  return (
    <FieldRootContext.Provider value={true}>
      <FieldPrimitive.Root
        data-slot="field"
        data-orientation={orientation}
        invalid={invalid ?? dataInvalid}
        className={cnState(fieldVariants({ orientation }), className)}
        {...props}
      />
    </FieldRootContext.Provider>
  );
}

/**
 * Scopes label association per item inside a Field that holds multiple
 * controls (checkbox/radio groups) — see Base UI Field.Item. Without it, the
 * field-level label wins the accessible name of every wrapped control.
 */
function FieldItem({ ...props }: FieldPrimitive.Item.Props) {
  return <FieldPrimitive.Item data-slot="field-item" {...props} />;
}

function FieldContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-content"
      className={cn("group/field-content flex flex-1 flex-col gap-1 leading-snug", className)}
      {...props}
    />
  );
}

function FieldLabel({ className, ...props }: React.ComponentProps<typeof Label>) {
  const inField = React.useContext(FieldRootContext);
  const classes = cn(
    // Checked state styles
    `has-data-checked:bg-primary/5 has-data-checked:border-primary
    dark:has-data-checked:bg-primary/10`,
    // Group and peer classes
    "group/field-label peer/field-label",
    // Layout
    "flex w-fit gap-2 leading-snug",
    // Disabled state
    "group-data-disabled/field:opacity-50",
    // Nested field styles
    `has-[>[data-slot=field]]:rounded-md has-[>[data-slot=field]]:border
    [&>*]:data-[slot=field]:p-3`,
    // Nested field layout
    "has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col",
    className,
  );

  // Inside a Field, Base UI associates the label with the field's control
  // (htmlFor) automatically; an explicit htmlFor prop still wins. Standalone
  // usage keeps the plain <label>.
  if (inField) {
    return (
      <FieldPrimitive.Label
        data-slot="field-label"
        render={<Label />}
        className={classes}
        {...props}
      />
    );
  }
  return <Label data-slot="field-label" className={classes} {...props} />;
}

function FieldTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-label"
      className={cn(
        `flex w-fit items-center gap-2 text-sm leading-snug font-medium
        group-data-disabled/field:opacity-50`,
        className,
      )}
      {...props}
    />
  );
}

function FieldDescription({ className, ...props }: React.ComponentProps<"p">) {
  const inField = React.useContext(FieldRootContext);
  const classes = cn(
    `text-muted-foreground text-left text-sm leading-normal font-normal
    group-has-[[data-orientation=horizontal]]/field:text-balance
    [[data-variant=legend]+&]:-mt-1.5`,
    "last:mt-0 nth-last-2:-mt-1",
    "[&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4",
    className,
  );

  // Inside a Field, Base UI links the description to the control via
  // aria-describedby automatically.
  if (inField) {
    return (
      <FieldPrimitive.Description data-slot="field-description" className={classes} {...props} />
    );
  }
  return <p data-slot="field-description" className={classes} {...props} />;
}

const fieldSeparatorVariants = cva("relative group-data-[variant=outline]/field-group:-mb-2", {
  variants: {
    size: {
      sm: "-my-1 h-12 text-xs",
      default: "-my-2 h-20 text-sm",
      lg: "-my-3 h-40 text-base",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

function FieldSeparator({
  children,
  className,
  size = "default",
  ...props
}: React.ComponentProps<"div"> & {
  children?: React.ReactNode;
} & VariantProps<typeof fieldSeparatorVariants>) {
  return (
    <div
      data-slot="field-separator"
      data-content={!!children}
      className={cn(fieldSeparatorVariants({ size }), className)}
      {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children && (
        <span
          className="text-muted-foreground bg-background relative mx-auto block
            w-fit px-2"
          data-slot="field-separator-content"
        >
          {children}
        </span>
      )}
    </div>
  );
}

function FieldError({
  className,
  children,
  errors,
  ...props
}: React.ComponentProps<"div"> & {
  errors?: Array<{ message?: string } | undefined>;
}) {
  const inField = React.useContext(FieldRootContext);
  const content = useMemo(() => {
    if (children) {
      return children;
    }

    if (!errors?.length) {
      return null;
    }

    const uniqueErrors = [...new Map(errors.map((error) => [error?.message, error])).values()];

    if (uniqueErrors?.length == 1) {
      return uniqueErrors[0]?.message;
    }

    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {uniqueErrors.map((error, index) => error?.message && <li key={index}>{error.message}</li>)}
      </ul>
    );
  }, [children, errors]);

  if (!content) {
    return null;
  }

  const classes = cn("text-destructive text-sm font-normal", className);

  // Inside a Field, Base UI links the error to the control via
  // aria-describedby. match={true}: visibility stays caller-driven (we already
  // returned null above when there is nothing to show).
  if (inField) {
    return (
      <FieldPrimitive.Error
        match={true}
        role="alert"
        data-slot="field-error"
        className={classes}
        {...props}
      >
        {content}
      </FieldPrimitive.Error>
    );
  }

  return (
    <div role="alert" data-slot="field-error" className={classes} {...props}>
      {content}
    </div>
  );
}

export {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldItem,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
};
